import { createContext } from "react"
import {
  HICState,
  HICConfig,
  StationConfig,
  StationSensors,
  UCPayload,
  UUID,
  LugsafysState,
} from "../../generated/proto-ts/main"
import { Action, MAX_MESSAGE_QUEUE_SIZE } from "../common/usercommUtils"

interface IUsercommContextProviderState {
  socket: WebSocket | undefined
  socketIsConnected: boolean
  bleDevice: BluetoothDevice | undefined
  bleIsConnected: boolean

  hicState: HICState | null
  lugsafysState: LugsafysState | null
  stationSensors: StationSensors | null

  hicRawMeasurementConsumable: UUID | null

  recvMessageQueue: UCPayload[]
  emitMessageQueue: UCPayload[]

  emitMtu: number
  recvMtu: number
}

export interface IUsercommContextProviderContextType extends IUsercommContextProviderState {
  // setSocket: (socket: WebSocket) => void
  setSocketIsConnected: (isConnected: boolean) => void
  setBleDevice: (device: BluetoothDevice | undefined) => void
  setBleIsConnected: (isConnected: boolean) => void

  emitDropHIC: () => void
  emitRestartStation: () => void
  emitRebootStation: () => void
  emitHaltStation: () => void

  consumeHICRawMeasurement: () => void

  consumeRecvMessage: (uuid: UCPayload["uuid"]) => void
  addEmitMessage: (payload: UCPayload) => void

  bleIsPaired: boolean
  bleIsConnecting: boolean
  connectBleGatt: (bleDevice: BluetoothDevice) => Promise<void>
  disconnectBleGatt: () => Promise<void>
  forgetBleGatt: () => Promise<void>
}

export const usercommStateReducer = (
  state: IUsercommContextProviderState,
  action: Action,
): IUsercommContextProviderState => {
  switch (action.type) {
    case "SET_HIC_STATE":
      return {
        ...state,
        hicState: action.payload,
      } satisfies typeof state
    case "SET_LUGSAFYS_STATE":
      return {
        ...state,
        lugsafysState: action.payload,
      } satisfies typeof state
    case "SET_STATION_SENSORS":
      return {
        ...state,
        stationSensors: action.payload,
      } satisfies typeof state
    case "SET_COMBINED_STATION_SENSORS_AND_HIC_STATE":
      return {
        ...state,
        hicState: action.payload.hicState,
        stationSensors: action.payload.stationSensors,
        lugsafysState: action.payload.lugsafysState,
      } satisfies typeof state
    case "SET_HIC_RAW_MEASUREMENT_CONSUMABLE":
      return {
        ...state,
        hicRawMeasurementConsumable: action.payload,
      } satisfies typeof state
    case "ADD_MESSAGE_TO_RECV_QUEUE":
      let newMessageQueue = [...state.recvMessageQueue, action.payload]
      if (newMessageQueue.length > MAX_MESSAGE_QUEUE_SIZE) {
        newMessageQueue.shift()
      }
      return {
        ...state,
        recvMessageQueue: newMessageQueue,
      } satisfies typeof state
    case "CONSUME_MESSAGE_FROM_RECV_QUEUE":
      const messageUuid = action.payload
      const updatedMessages = state.recvMessageQueue.filter(
        (message) => message.uuid !== messageUuid,
      )
      return {
        ...state,
        recvMessageQueue: updatedMessages,
      } satisfies typeof state
    case "ADD_MESSAGE_TO_EMIT_QUEUE":
      let newEmitMessageQueue = [...state.emitMessageQueue, action.payload]
      if (newEmitMessageQueue.length > MAX_MESSAGE_QUEUE_SIZE) {
        newEmitMessageQueue.shift()
      }
      return {
        ...state,
        emitMessageQueue: newEmitMessageQueue,
      } satisfies typeof state
    case "CONSUME_MESSAGE_FROM_EMIT_QUEUE":
      const emitMessageUuid = action.payload
      const updatedEmitMessages = state.emitMessageQueue.filter(
        (message) => message.uuid !== emitMessageUuid,
      )
      return {
        ...state,
        emitMessageQueue: updatedEmitMessages,
      } satisfies typeof state
    case "SET_BLE_DEVICE":
      return {
        ...state,
        bleDevice: action.payload,
      } satisfies typeof state
    case "SET_BLE_IS_CONNECTED":
      if (state.bleIsConnected === action.payload) {
        return state // bail out — no re-render when value is unchanged
      }
      return {
        ...state,
        bleIsConnected: action.payload,
      } satisfies typeof state
    case "SET_SOCKET":
      return {
        ...state,
        socket: action.payload,
      } satisfies typeof state
    case "SET_SOCKET_IS_CONNECTED":
      return {
        ...state,
        socketIsConnected: action.payload,
      } satisfies typeof state
    case "SET_EMIT_MTU":
      return {
        ...state,
        emitMtu: action.payload,
      } satisfies typeof state
    case "SET_RECV_MTU":
      return {
        ...state,
        recvMtu: action.payload,
      } satisfies typeof state
    default:
      console.warn(`usercommStateReducer: unknown action type:`, action)
      return state
  }
}

export const usercommContext = createContext<IUsercommContextProviderContextType | undefined>(
  undefined,
)
